// Iter 71 — Vendor Master · Add / Edit form (Phase 1).
// Single page covers both create and update — distinguished by the
// route param. Sections rendered as an accordion (operator decision 3a).
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    FloppyDisk, ArrowLeft, IdentificationCard, Phone, ShieldCheck,
    MapPin, Coin, Bank, Note,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import {
    Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../../components/ui/accordion";
import { toast } from "sonner";
import { CATEGORY_LABEL, VENDOR_TYPE_LABEL, MSME_LABEL,
    PAYMENT_METHOD_LABEL, TDS_LABEL, DEFAULT_ENUMS } from "./vendor_constants";

const BLANK = {
    name: "",
    category: "other",
    vendor_type: "company",
    active: true,
    contact_person: "", designation: "", email: "", mobile: "", alternate_contact: "",
    pan: "", gstin: "",
    msme_status: "not_registered",
    tds_category: "not_applicable",
    address_line1: "", address_line2: "", city: "", state: "", country: "India", pincode: "",
    payment_terms: "", credit_period_days: 0, currency: "INR", preferred_payment_method: "bank_transfer",
    bank_name: "", account_number: "", ifsc: "", beneficiary_name: "",
    website: "", notes: "",
    master_agreement_start_date: "", master_agreement_end_date: "",
};

export default function VendorForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const [form, setForm] = useState(BLANK);
    const [enums, setEnums] = useState(DEFAULT_ENUMS);
    const [loading, setLoading] = useState(isEdit);
    const [saving, setSaving] = useState(false);

    // Load enums + (if editing) existing vendor
    useEffect(() => {
        let cancelled = false;
        const enumsReq = api.get("/vendors/meta/enums");
        const vendorReq = isEdit ? api.get(`/vendors/${id}`) : Promise.resolve(null);
        Promise.all([enumsReq, vendorReq])
            .then(([enumsRes, vendorRes]) => {
                if (cancelled) return;
                setEnums({ ...DEFAULT_ENUMS, ...enumsRes.data });
                if (vendorRes) {
                    const v = vendorRes.data || {};
                    setForm({
                        ...BLANK,
                        ...Object.fromEntries(
                            Object.keys(BLANK).map((k) => [k, v[k] !== undefined && v[k] !== null ? v[k] : BLANK[k]])
                        ),
                    });
                }
            })
            .catch((err) => {
                if (cancelled) return;
                toast.error(formatApiError(err, "Failed to load form data"));
                if (isEdit) navigate("/vendors");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => { cancelled = true; };
    }, [id, isEdit, navigate]);

    const set = (k) => (e) => {
        const v = e?.target?.type === "checkbox" ? e.target.checked : e?.target?.value;
        setForm((f) => ({ ...f, [k]: v }));
    };
    const setNum = (k) => (e) => {
        const raw = e.target.value;
        setForm((f) => ({ ...f, [k]: raw === "" ? 0 : Number(raw) }));
    };

    const submit = async () => {
        if (!form.name.trim()) {
            toast.error("Vendor name is required");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form };
            // Empty strings preserved; backend treats "" as not-provided.
            const res = isEdit
                ? await api.put(`/vendors/${id}`, payload)
                : await api.post("/vendors", payload);
            toast.success(isEdit ? "Vendor updated" : `Vendor ${res.data.code} created`);
            navigate(`/vendors/${res.data.id}`);
        } catch (err) {
            toast.error(formatApiError(err, "Save failed"));
        } finally {
            setSaving(false);
        }
    };

    const sections = useMemo(() => ([
        {
            id: "basic", label: "Basic Information", icon: IdentificationCard,
            render: () => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Vendor Name *" testId="f-name">
                        <Input data-testid="f-name" value={form.name} onChange={set("name")} maxLength={200} />
                    </Field>
                    <Field label="Vendor Category *" testId="f-category">
                        <SelectBox value={form.category} onChange={set("category")} testId="f-category"
                            options={enums.categories.map((c) => [c, CATEGORY_LABEL[c] || c])} />
                    </Field>
                    <Field label="Vendor Type *" testId="f-vendor-type">
                        <SelectBox value={form.vendor_type} onChange={set("vendor_type")} testId="f-vendor-type"
                            options={enums.vendor_types.map((c) => [c, VENDOR_TYPE_LABEL[c] || c])} />
                    </Field>
                    <Field label="Status">
                        <label className="inline-flex items-center gap-2 mt-2 text-sm">
                            <input type="checkbox" data-testid="f-active" checked={form.active}
                                onChange={set("active")} className="h-4 w-4" />
                            <span className="text-slate-700">Active</span>
                        </label>
                    </Field>
                </div>
            ),
        },
        {
            id: "contact", label: "Contact Information", icon: Phone,
            render: () => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Contact Person"><Input data-testid="f-contact-person" value={form.contact_person} onChange={set("contact_person")} /></Field>
                    <Field label="Designation"><Input data-testid="f-designation" value={form.designation} onChange={set("designation")} /></Field>
                    <Field label="Email"><Input data-testid="f-email" type="email" value={form.email} onChange={set("email")} /></Field>
                    <Field label="Mobile"><Input data-testid="f-mobile" value={form.mobile} onChange={set("mobile")} placeholder="+91XXXXXXXXXX" /></Field>
                    <Field label="Alternate Contact"><Input data-testid="f-alt-contact" value={form.alternate_contact} onChange={set("alternate_contact")} /></Field>
                </div>
            ),
        },
        {
            id: "tax", label: "Tax & Compliance", icon: ShieldCheck,
            render: () => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="PAN" hint="Format: AAAAA9999A">
                        <Input data-testid="f-pan" value={form.pan} onChange={set("pan")} maxLength={10}
                            className="font-mono uppercase" />
                    </Field>
                    <Field label="GSTIN" hint="Format: 27AAAAA9999A1Z5">
                        <Input data-testid="f-gstin" value={form.gstin} onChange={set("gstin")} maxLength={15}
                            className="font-mono uppercase" />
                    </Field>
                    <Field label="MSME Status">
                        <SelectBox value={form.msme_status} onChange={set("msme_status")} testId="f-msme"
                            options={enums.msme_statuses.map((c) => [c, MSME_LABEL[c] || c])} />
                    </Field>
                    <Field label="TDS Category">
                        <SelectBox value={form.tds_category} onChange={set("tds_category")} testId="f-tds"
                            options={enums.tds_categories.map((c) => [c, TDS_LABEL[c] || c])} />
                    </Field>
                </div>
            ),
        },
        {
            id: "address", label: "Address", icon: MapPin,
            render: () => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Address Line 1" colSpan={2}><Input data-testid="f-addr1" value={form.address_line1} onChange={set("address_line1")} /></Field>
                    <Field label="Address Line 2" colSpan={2}><Input data-testid="f-addr2" value={form.address_line2} onChange={set("address_line2")} /></Field>
                    <Field label="City"><Input data-testid="f-city" value={form.city} onChange={set("city")} /></Field>
                    <Field label="State">
                        <SelectBox value={form.state} onChange={set("state")} testId="f-state"
                            options={[["", "Select state…"], ...enums.indian_states.map((s) => [s, s])]} />
                    </Field>
                    <Field label="Country"><Input data-testid="f-country" value={form.country} onChange={set("country")} /></Field>
                    <Field label="Pincode" hint="6 digits"><Input data-testid="f-pincode" value={form.pincode} onChange={set("pincode")} maxLength={6} /></Field>
                </div>
            ),
        },
        {
            id: "commercial", label: "Commercial Information", icon: Coin,
            render: () => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Payment Terms"><Input data-testid="f-pay-terms" value={form.payment_terms} onChange={set("payment_terms")} placeholder="Net 30 / Advance / On Receipt" /></Field>
                    <Field label="Credit Period (days)"><Input data-testid="f-credit-days" type="number" min={0} max={365} value={form.credit_period_days} onChange={setNum("credit_period_days")} /></Field>
                    <Field label="Currency">
                        <SelectBox value={form.currency} onChange={set("currency")} testId="f-currency"
                            options={enums.currencies.map((c) => [c, c])} />
                    </Field>
                    <Field label="Preferred Payment Method">
                        <SelectBox value={form.preferred_payment_method} onChange={set("preferred_payment_method")} testId="f-pay-method"
                            options={enums.payment_methods.map((c) => [c, PAYMENT_METHOD_LABEL[c] || c])} />
                    </Field>
                    <Field label="Master Agreement Start" hint="Umbrella MSA / framework agreement start"><Input data-testid="f-master-start" type="date" value={form.master_agreement_start_date || ""} onChange={set("master_agreement_start_date")} /></Field>
                    <Field label="Master Agreement End" hint="Per-service dates live on the Services tab"><Input data-testid="f-master-end" type="date" value={form.master_agreement_end_date || ""} onChange={set("master_agreement_end_date")} /></Field>
                </div>
            ),
        },
        {
            id: "bank", label: "Bank Details", icon: Bank,
            render: () => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Field label="Bank Name"><Input data-testid="f-bank-name" value={form.bank_name} onChange={set("bank_name")} /></Field>
                    <Field label="Account Number"><Input data-testid="f-account" value={form.account_number} onChange={set("account_number")} /></Field>
                    <Field label="IFSC" hint="Format: AAAA0000000">
                        <Input data-testid="f-ifsc" value={form.ifsc} onChange={set("ifsc")} maxLength={11} className="font-mono uppercase" />
                    </Field>
                    <Field label="Beneficiary Name"><Input data-testid="f-beneficiary" value={form.beneficiary_name} onChange={set("beneficiary_name")} /></Field>
                </div>
            ),
        },
        {
            id: "additional", label: "Additional Information", icon: Note,
            render: () => (
                <div className="grid grid-cols-1 gap-4">
                    <Field label="Website"><Input data-testid="f-website" value={form.website} onChange={set("website")} placeholder="https://example.com" /></Field>
                    <Field label="Notes">
                        <Textarea data-testid="f-notes" rows={4} value={form.notes} onChange={set("notes")} />
                    </Field>
                    {isEdit && (
                        <div className="text-xs text-slate-500 mt-2">
                            Document attachments can be managed from the Vendor Detail page after saving.
                        </div>
                    )}
                </div>
            ),
        },
    ]), [form, enums, isEdit]);

    if (loading) return <div className="px-8 py-12 text-slate-500" data-testid="vendor-form-loading">Loading…</div>;

    return (
        <div className="px-6 sm:px-8 py-6 max-w-4xl" data-testid="vendor-form-page">
            <PageHeader
                eyebrow={isEdit ? "Edit vendor" : "Onboard a new vendor"}
                title={isEdit ? form.name || "Vendor" : "Add Vendor"}
                description="All fields except Vendor Name, Category and Type are optional in Phase 1, but PAN / GSTIN / IFSC / Pincode / Mobile / Email / Website are format-validated when provided."
                actions={
                    <div className="flex items-center gap-2">
                        <Button data-testid="vendor-cancel-btn" variant="outline" onClick={() => navigate(isEdit ? `/vendors/${id}` : "/vendors")}>
                            <ArrowLeft size={16} /> Cancel
                        </Button>
                        <Button data-testid="vendor-save-btn" onClick={submit} disabled={saving} className="gap-2">
                            <FloppyDisk size={16} weight="bold" />
                            {saving ? "Saving…" : isEdit ? "Update Vendor" : "Create Vendor"}
                        </Button>
                    </div>
                }
            />

            <div className="mt-6">
                <Accordion type="multiple" defaultValue={["basic"]} className="space-y-3" data-testid="vendor-form-sections">
                    {sections.map(({ id: sid, label, icon: Icon, render }) => (
                        <AccordionItem key={sid} value={sid} className="bg-white border border-slate-200 rounded-lg" data-testid={`vendor-section-${sid}`}>
                            <AccordionTrigger className="px-4 hover:no-underline">
                                <div className="flex items-center gap-3 text-left">
                                    <Icon size={18} className="text-slate-500" />
                                    <span className="font-medium text-slate-900">{label}</span>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-5 pt-1 border-t border-slate-100">
                                <div className="mt-3">{render()}</div>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </div>
        </div>
    );
}

function Field({ label, hint, children, colSpan }) {
    return (
        <div className={colSpan === 2 ? "md:col-span-2" : ""}>
            <Label className="text-xs text-slate-600 mb-1.5 inline-block">{label}</Label>
            {children}
            {hint && <div className="text-[11px] text-slate-400 mt-1">{hint}</div>}
        </div>
    );
}

function SelectBox({ value, onChange, options, testId }) {
    return (
        <select
            data-testid={testId}
            value={value}
            onChange={onChange}
            className="flex h-9 w-full rounded-md border border-slate-300 bg-white px-3 py-1 text-sm shadow-sm"
        >
            {options.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
            ))}
        </select>
    );
}
